import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Globe, CheckCircle2, ArrowRight, Palette, Search, Shield, Zap, Users } from 'lucide-react';

const Webseiten = () => {
  const { t } = useTranslation();
  
  const features = [
    { icon: Palette, title: t('webseiten.feature1Title'), desc: t('webseiten.feature1Desc') },
    { icon: Search, title: t('webseiten.feature2Title'), desc: t('webseiten.feature2Desc') },
    { icon: Shield, title: t('webseiten.feature3Title'), desc: t('webseiten.feature3Desc') },
    { icon: Zap, title: t('webseiten.feature4Title'), desc: t('webseiten.feature4Desc') },
  ];

  const packages = [
    { name: t('webseiten.package1Name'), price: '990', pages: t('webseiten.package1Pages'), features: [t('webseiten.package1Feature1'), t('webseiten.package1Feature2'), t('webseiten.package1Feature3'), t('webseiten.package1Feature4')] },
    { name: t('webseiten.package2Name'), price: '2.490', pages: t('webseiten.package2Pages'), features: [t('webseiten.package2Feature1'), t('webseiten.package2Feature2'), t('webseiten.package2Feature3'), t('webseiten.package2Feature4')], popular: true },
    { name: t('webseiten.package3Name'), price: '4.990', pages: t('webseiten.package3Pages'), features: [t('webseiten.package3Feature1'), t('webseiten.package3Feature2'), t('webseiten.package3Feature3'), t('webseiten.package3Feature4')] },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-medium mb-6">
              <Globe size={16} />
              {t('webseiten.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('webseiten.title')}
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              {t('webseiten.description')}
            </p>
            <Link to="/kontakt">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                {t('webseiten.ctaButton')}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900">{t('webseiten.featuresTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <Card key={i} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <f.icon className="text-blue-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900">{t('webseiten.packagesTitle')}</h2>
            <p className="text-slate-600 mt-3">{t('webseiten.packagesSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, i) => (
              <Card key={i} className={`border-2 ${pkg.popular ? 'border-blue-600 shadow-xl' : 'border-slate-200'} relative`}>
                {pkg.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm">{t('webseiten.popular')}</div>}
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900">{pkg.name}</h3>
                  <p className="text-slate-500 text-sm mb-4">{pkg.pages}</p>
                  <div className="text-3xl font-bold text-slate-900 mb-6">{pkg.price}€</div>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="text-green-500" size={16} />{f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/kontakt">
                    <Button className={`w-full ${pkg.popular ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'} text-white`}>
                      {t('buttons.requestQuote')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">{t('webseiten.ctaTitle')}</h2>
          <p className="text-blue-100 mb-8">{t('webseiten.ctaSubtitle')}</p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
              {t('buttons.startProject')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Webseiten;
